<template>
	<PageHeaderGeneralSkeleton
		v-editable="blok"
		:title="title ?? blok.title"
		:subtitle="subtitle ?? blok.subtitle"
		:hide-title="hideTitle"
	>
		<template #aside>
			<slot name="aside" />
		</template>

		<template #image>
			<NuxtPicture
				v-if="resolvedImage?.filename"
				:src="resolvedImage.filename"
				:img-attrs="{
					class:'object-cover w-full h-full',
					style: imgStyles,
				}"
				class="absolute inset-0 z-10 pointer-events-none"
				:provider="provider"
				height="400"
				width="2560"
				sizes="sm:768px md:1280px lg:1920px"
				:modifiers="modifiers"
				loading="eager"
				alt=""
			/>
		</template>
	</PageHeaderGeneralSkeleton>
</template>

<script setup lang="ts">
	import type {PageTypeGeneralStoryblok} from '@/storyblok/types';
	import type {ArticleHeroImage} from '~/resources/articleHeroImages';

	interface Props {
		blok: Pick<PageTypeGeneralStoryblok, 'title' | 'subtitle' | 'image'>
		image?: ArticleHeroImage;
		hideTitle?: boolean;
		title?: string;
		subtitle?: string;
	}

	const props = defineProps<Props>();

	const resolvedImage = computed(() => props.image ?? props.blok.image);

	const provider = computed(() => props.image ? 'ipx' : 'storyblok');

	const modifiers = computed(() => (
		resolvedImage.value?.focus
			? provider.value === 'storyblok'
				? {filters: {focal: resolvedImage.value.focus}}
				: {}
			: {}
	));

	// IPX does not support focus points, so we need to use object-position styles instead
	const imgStyles = computed(() => {
		if (!resolvedImage.value || provider.value === 'storyblok') {
			return {};
		}

		const {width = 600, height = 400, focus} = resolvedImage.value;
		const [, focusX, focusY] = focus?.match(/(\d+)x(\d+)/) ?? [];
		const fx = focusX ? parseInt(focusX) : width / 2;
		const fy = focusY ? parseInt(focusY) : height / 2;

		return {objectPosition: `${fx / width * 100}% ${fy / height * 100}%`};
	});
</script>
